/*
  Murlong Cres — currency-aware free shipping.

  The theme's built-in free-shipping bar treats the threshold as a fixed number
  in whatever currency the customer browses in (so 3800 kr shows as "$3800").
  These two custom elements instead convert the shop-base threshold into the
  customer's presentment currency using Shopify.currency.rate, so the amount is
  correct in every market.

  Self-contained, so a Prestige theme update won't touch it.
*/
(function () {
  function rate() { try { return parseFloat(window.Shopify.currency.rate) || 1; } catch (e) { return 1; } }
  function activeCurrency() { try { return window.Shopify.currency.active; } catch (e) { return 'NOK'; } }
  function locale() { try { return window.Shopify.locale || 'en'; } catch (e) { return 'en'; } }

  function fmt(cents, currency) {
    var amount = cents / 100;
    try {
      // Norwegian home market reads "3 800 kr", not "NOK 3,800".
      if (currency === 'NOK') {
        return new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(amount) + ' kr';
      }
      return new Intl.NumberFormat(locale(), { style: 'currency', currency: currency, maximumFractionDigits: 0, currencyDisplay: 'narrowSymbol' }).format(amount);
    } catch (e) { return Math.round(amount) + ' ' + currency; }
  }

  // shop-base major units -> presentment cents
  function thresholdCents(el) {
    var base = parseFloat(el.getAttribute('base-threshold')) || 0;
    return Math.round(base * rate() * 100);
  }

  var MCFreeShippingBar = class extends HTMLElement {
    connectedCallback() {
      this._handler = this.render.bind(this);
      document.addEventListener('cart:change', this._handler);
      this.render();
    }
    disconnectedCallback() { document.removeEventListener('cart:change', this._handler); }
    render(evt) {
      var self = this;
      var thr = thresholdCents(this);
      var total = parseFloat(this.getAttribute('total-price'));
      if (isNaN(total) && evt && evt.detail && evt.detail.cart) total = evt.detail.cart.total_price;
      var draw = function (totalCents) {
        var cur = activeCurrency();
        var remaining = Math.max(0, thr - totalCents);
        var pct = thr > 0 ? Math.min(100, Math.round((totalCents / thr) * 100)) : 100;
        var msg;
        if (remaining <= 0) {
          msg = self.getAttribute('reached-message') || 'You are eligible for free shipping.';
        } else {
          msg = (self.getAttribute('unreached-message') || 'Spend {{ remaining_amount }} more and get free shipping!')
            .replace(/\{\{\s*remaining_amount\s*\}\}/g, fmt(remaining, cur));
        }
        self.innerHTML =
          '<p class="mc-fsb__msg">' + msg + '</p>' +
          '<span class="mc-fsb__track"><span class="mc-fsb__fill" style="width:' + pct + '%"></span></span>';
      };
      if (isNaN(total)) {
        fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) { draw(c.total_price); }).catch(function () { draw(0); });
      } else {
        draw(total);
      }
    }
  };

  // Announcement-bar amount: converts the shop-base free-shipping threshold into
  // the customer's currency and drops it inline (e.g. "Complimentary shipping over $409").
  var MCShipAmount = class extends HTMLElement {
    connectedCallback() {
      this.textContent = fmt(thresholdCents(this), activeCurrency());
    }
  };

  var MCFreeShippingNote = class extends HTMLElement {
    connectedCallback() {
      var span = this.querySelector('.mc-fsn__text');
      if (!span) return;
      var label = this.getAttribute('label') || 'Free shipping on orders over {{ amount }}';
      span.textContent = label.replace(/\{\{\s*amount\s*\}\}/g, fmt(thresholdCents(this), activeCurrency()));
    }
  };

  // Real review proof, read from the page's JSON-LD aggregateRating (Trustoo).
  // Hides itself if the product has no reviews, so it never shows a fake number.
  var MCReviewProof = class extends HTMLElement {
    connectedCallback() {
      var r = this.findRating();
      var span = this.querySelector('.mc-review-proof__text');
      if (!r || !r.count || !span) { this.style.display = 'none'; return; }
      span.innerHTML = '<span class="mc-review-proof__stars">★★★★★</span> ' +
        r.value + ' · ' + r.count + ' reviews';
    }
    findRating() {
      var nodes = document.querySelectorAll('script[type="application/ld+json"]');
      for (var i = 0; i < nodes.length; i++) {
        try { var found = dig(JSON.parse(nodes[i].textContent)); if (found) return found; } catch (e) {}
      }
      return null;
      function dig(o) {
        if (!o || typeof o !== 'object') return null;
        if (o.aggregateRating) return { value: o.aggregateRating.ratingValue, count: o.aggregateRating.reviewCount };
        for (var k in o) { var r = dig(o[k]); if (r) return r; }
        return null;
      }
    }
  };

  // Free-shipping upsell shown in the cart drawer / cart page: when the customer is
  // within one Garment Bag of the free-shipping threshold, offer to add the bag to
  // cross it. Uses the same currency math as the bar, and adds to cart via the exact
  // flow the theme's own product form uses (sections API + cart:change) so the drawer
  // refreshes correctly.
  var MCFreeShipUpsell = class extends HTMLElement {
    connectedCallback() {
      this._handler = this.onCart.bind(this);
      document.addEventListener('cart:change', this._handler);
      var btn = this.querySelector('[data-add]');
      if (btn) btn.addEventListener('click', this.addBag.bind(this));
      this.fetchAndApply();
    }
    disconnectedCallback() { document.removeEventListener('cart:change', this._handler); }
    onCart(evt) {
      var cart = evt && evt.detail && evt.detail.cart;
      if (cart && typeof cart.total_price === 'number') { this.apply(cart); } else { this.fetchAndApply(); }
    }
    fetchAndApply() {
      var self = this;
      fetch(window.Shopify.routes.root + 'cart.js').then(function (r) { return r.json(); }).then(function (c) { self.apply(c); }).catch(function () {});
    }
    apply(cart) {
      var r = rate();
      var thr = Math.round((parseFloat(this.getAttribute('base-threshold')) || 0) * r * 100); // presentment cents
      var bagCents = Math.round((parseFloat(this.getAttribute('bag-price')) || 0) * r);        // base cents -> presentment cents
      var total = (cart && typeof cart.total_price === 'number') ? cart.total_price : 0;
      var items = (cart && cart.items) || [];
      var bagVariant = this.getAttribute('bag-variant-id');
      var bagHandle = this.getAttribute('bag-handle');
      var inCart = items.some(function (it) { return String(it.variant_id) === String(bagVariant) || it.handle === bagHandle; });
      var remaining = thr - total;
      var show = total > 0 && remaining > 0 && remaining <= bagCents && !inCart;
      this.hidden = !show;
      if (show) {
        var cur = activeCurrency();
        var span = this.querySelector('[data-remaining]');
        if (span) span.textContent = fmt(remaining, cur);
        var bp = this.querySelector('[data-bagprice]');
        if (bp) bp.textContent = fmt(bagCents, cur);
      }
    }
    async addBag(e) {
      if (e) e.preventDefault();
      var variantId = this.getAttribute('bag-variant-id');
      if (!variantId) return;
      var btn = this.querySelector('[data-add]');
      if (btn) { if (btn.getAttribute('aria-busy') === 'true') return; btn.setAttribute('aria-busy', 'true'); }
      var sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
      var fd = new FormData();
      fd.set('id', variantId);
      fd.set('quantity', '1');
      fd.set('sections', sections.join(','));
      fd.set('sections_url', window.location.pathname);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      try {
        var res = await fetch(window.Shopify.routes.root + 'cart/add.js', { body: fd, method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        var rj = await res.json();
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        if (res.ok) {
          var cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
          cart.sections = rj.sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } }));
        }
      } catch (err) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
      if (btn) btn.removeAttribute('aria-busy');
    }
  };

  // Product-page shipping estimate: shows the shopper's regional flat rate plus
  // the free-shipping threshold, converted into their currency (same engine as
  // the free-shipping bar). The rates live in Shopify's shipping settings, in
  // NOK; the base-* attributes below MUST be kept in sync with them:
  //   Norway kr99 (home) · Europe kr350 · USA/Australia/rest-of-world kr500 · free over kr5000.
  // Re-renders itself on every variant change because connectedCallback re-fires
  // when Prestige re-inserts the element.
  var MC_EUROPE = {AX:1,AL:1,AD:1,AT:1,BE:1,BA:1,BG:1,HR:1,CY:1,CZ:1,DK:1,EE:1,FO:1,FI:1,FR:1,DE:1,GI:1,GR:1,GG:1,HU:1,IS:1,IE:1,IM:1,IT:1,JE:1,XK:1,LV:1,LI:1,LT:1,LU:1,MT:1,MC:1,ME:1,NL:1,MK:1,PL:1,PT:1,RO:1,SM:1,RS:1,SK:1,SI:1,ES:1,SE:1,CH:1,UA:1,GB:1,VA:1,BY:1,MD:1};
  var MCShippingEstimate = class extends HTMLElement {
    connectedCallback() { this.render(); }
    render() {
      try {
        var country  = (this.getAttribute('data-country') || '').toUpperCase();
        var baseNo   = parseFloat(this.getAttribute('base-no'))  || 0;
        var baseEu   = parseFloat(this.getAttribute('base-eu'))  || 0;
        var baseRow  = parseFloat(this.getAttribute('base-row')) || 0;
        var baseThr  = parseFloat(this.getAttribute('base-threshold')) || 0;
        var isNo     = country === 'NO';
        var baseRate = isNo ? baseNo : (MC_EUROPE[country] ? baseEu : baseRow);
        var slow     = !isNo; // Norway 1–3 days, everywhere else 3–5 days
        var r = rate(), cur = activeCurrency();
        var rateStr = fmt(Math.round(baseRate * r * 100), cur);
        var thrStr  = fmt(Math.round(baseThr  * r * 100), cur);
        var tShip = this.getAttribute('t-ship') || 'Shipping';
        var tFree = this.getAttribute('t-free') || 'free over';
        var tFrom = this.getAttribute('t-from') || '';
        var speed = this.getAttribute(slow ? 't-slow' : 't-fast') || '';
        var ratePart = (isNo && tFrom ? (tFrom + ' ') : '') + rateStr;
        this.innerHTML =
          '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 7h11v8H3zM14 10h4l3 3v2h-7z" stroke-linejoin="round"/><circle cx="7" cy="17" r="1.6"/><circle cx="17.5" cy="17" r="1.6"/></svg>'
          + '<span>' + tShip + ': <strong>' + ratePart + '</strong> · ' + tFree + ' ' + thrStr + ' · ' + speed + '</span>';
      } catch (e) { this.style.display = 'none'; }
    }
  };
  if (!window.customElements.get('mc-shipping-estimate')) window.customElements.define('mc-shipping-estimate', MCShippingEstimate);

  if (!window.customElements.get('mc-free-ship-upsell')) window.customElements.define('mc-free-ship-upsell', MCFreeShipUpsell);
  if (!window.customElements.get('mc-ship-amount')) window.customElements.define('mc-ship-amount', MCShipAmount);
  if (!window.customElements.get('mc-free-shipping-bar')) window.customElements.define('mc-free-shipping-bar', MCFreeShippingBar);
  if (!window.customElements.get('mc-free-shipping-note')) window.customElements.define('mc-free-shipping-note', MCFreeShippingNote);
  if (!window.customElements.get('mc-review-proof')) window.customElements.define('mc-review-proof', MCReviewProof);
})();
